import { FC, useEffect, useState } from 'react';
import {
  Stack,
  useTheme,
  useMediaQuery,
  Button,
  CircularProgress,
  Box,
  Divider,
} from '@mui/material';
import AdjustLearnHow from './AdjustProvider/AdjustLearnHow';
import AppsFlyerLearnHow from './AppsFlyerProvider/AppsFlyerLearnHow';
import { FormFieldI } from '../../../../../components/Form/FormField';
import AdjustDetails from './AdjustProvider/AdjustDetails';
import AppsFlyerDetails from './AppsFlyerProvider/AppsFlyerDetails';
import BigQueryDetails from './BigQueryProvider/BigQueryDetails';
import GCSBucketDetails from './GCSBucketProvider/GCSBucketDetails';
import MySQLDetails from './MySQLProvider/MySQLDetails';
import S3BucketDetails from './S3BucketProvider/S3BucketDetails';
import SnowflakeDetails from './SnowflakeProvider/SnowflakeDetails';
import GoogleDriveDetails from './GoogleDriveProvider/GoogleDriveDetails';
import { DataAccessProvider } from '@constants/constants';
import AggregatedCohortDataDetails from './AggregatedCohortDataProvider/AggregatedCohortDataDetails';

export interface AccessProviderConfig {
  header?: string;
  label: string;
  value: string;
  info?: JSX.Element;
  fields?: FormFieldI[];
  iapFields?: FormFieldI[];
}

interface GrantAccessToProviderProps {
  scrollTo: string;
  config: AccessProviderConfig | null;
  loading: boolean;
  onSaveAccess: (formData: Record<string, string | string[]>) => void;
  selfServeId: string;
}

const GrantAccessToProvider: FC<GrantAccessToProviderProps> = ({
  scrollTo,
  config,
  loading,
  onSaveAccess,
  selfServeId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [formValues, setFormValues] = useState<Record<string, string | string[]>>({});
  const [isSubmitEnabled, setIsSubmitEnabled] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const isOtherDataSource =
    config?.value !== undefined &&
    ![DataAccessProvider.ADJUST, DataAccessProvider.APPS_FLYER].includes(
      config.value as DataAccessProvider,
    );

  // Get all fields for validation
  const allFields = [...(config?.fields || []), ...(config?.iapFields || [])];

  useEffect(() => {
    if (scrollTo) {
      const element: HTMLElement | null = document.getElementById(scrollTo);
      if (!element) return;

      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Validate form whenever values change
  useEffect(() => {
    validateForm(formValues);
  }, [formValues]);

  const validateForm = (values: Record<string, string | string[]>) => {
    const allRequiredFieldsFilled = allFields.every(
      (field) => !field.required || (values[field.name] && values[field.name] !== ''),
    );

    setIsSubmitEnabled(allRequiredFieldsFilled);
  };

  const handleInputChange = (name: string, value: string | string[]) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    onSaveAccess(formValues);
  };

  const getLearnHowContent = () => {
    if (config?.value === DataAccessProvider.ADJUST) {
      return <AdjustLearnHow scrollTo={scrollTo} />;
    } else if (config?.value === DataAccessProvider.APPS_FLYER) {
      return <AppsFlyerLearnHow scrollTo={scrollTo} />;
    } else {
      return 'No Data';
    }
  };

  const getDataAccessDetailsCard = () => {
    if (config?.value === DataAccessProvider.ADJUST) {
      return (
        <AdjustDetails
          config={config}
          loading={loading}
          formValues={formValues}
          handleInputChange={handleInputChange}
          setIsFormValid={setIsFormValid}
        />
      );
    } else if (config?.value === DataAccessProvider.APPS_FLYER) {
      return (
        <AppsFlyerDetails
          config={config}
          loading={loading}
          formValues={formValues}
          handleInputChange={handleInputChange}
          setIsFormValid={setIsFormValid}
        />
      );
    } else if (config?.value === DataAccessProvider.BIGQUERY) {
      return (
        <BigQueryDetails
          loading={loading}
          formValues={formValues}
          handleInputChange={handleInputChange}
          setIsFormValid={setIsFormValid}
        />
      );
    } else if (config?.value === DataAccessProvider.GCS) {
      return (
        <GCSBucketDetails
          loading={loading}
          formValues={formValues}
          handleInputChange={handleInputChange}
          setIsFormValid={setIsFormValid}
        />
      );
    } else if (config?.value === DataAccessProvider.S3) {
      return (
        <S3BucketDetails
          loading={loading}
          formValues={formValues}
          handleInputChange={handleInputChange}
          setIsFormValid={setIsFormValid}
        />
      );
    } else if (config?.value === DataAccessProvider.MYSQL) {
      return (
        <MySQLDetails
          loading={loading}
          formValues={formValues}
          handleInputChange={handleInputChange}
          setIsFormValid={setIsFormValid}
        />
      );
    } else if (config?.value === DataAccessProvider.SNOWFLAKE) {
      return (
        <SnowflakeDetails
          loading={loading}
          formValues={formValues}
          handleInputChange={handleInputChange}
          setIsFormValid={setIsFormValid}
        />
      );
    } else if (config?.value === DataAccessProvider.GOOGLE_DRIVE) {
      return (
        <GoogleDriveDetails
          loading={loading}
          formValues={formValues}
          handleInputChange={handleInputChange}
          setIsFormValid={setIsFormValid}
        />
      );
    } else if (config?.value === DataAccessProvider.AGGREGATED_DATA) {
      return (
        <AggregatedCohortDataDetails
          selfServeId={selfServeId}
          formValues={formValues}
          handleInputChange={handleInputChange}
          setIsFormValid={setIsFormValid}
        />
      );
    } else {
      return config?.info || <>No Data</>;
    }
  };

  return (
    <Stack
      width={isOtherDataSource ? '100%' : '900px'}
      maxWidth={isMobile ? 'min(900px, 95vw)' : '900px'}
      height="100%"
    >
      {/* Body Section */}
      <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
        <Box
          sx={{
            // padding: "1rem 0.5rem",
            mx: '1rem',
            background: '#FFFFFF',
            borderRadius: '0.5rem',
            width: isMobile || isOtherDataSource ? '100%' : '50%',
          }}
        >
          {getDataAccessDetailsCard()}
        </Box>

        {!isMobile && !isOtherDataSource && <Divider orientation="vertical" />}

        {!isOtherDataSource && (
          <Box sx={{ height: '80vh', width: isMobile ? '100%' : '40%' }}>
            {getLearnHowContent()}
          </Box>
        )}
      </Box>

      {/* Footer Section */}
      <Stack padding={'1rem'} direction={'row'}>
        <Button
          disableElevation
          disableFocusRipple
          disableRipple
          disableTouchRipple
          variant="contained"
          onClick={handleSubmit}
          disabled={!isFormValid || !isSubmitEnabled || loading}
          sx={{
            padding: '0.625rem 1.5rem',
            borderRadius: '100px',
            textTransform: 'none',
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            fontWeight: 500,
            background: '#2F736E',
            color: '#fff',
          }}
        >
          {loading && <CircularProgress size={20} sx={{ marginRight: '0.5rem' }} />}
          {loading ? 'Confirming' : 'Confirm Access'}
        </Button>
        <Button
          disableElevation
          disableFocusRipple
          disableRipple
          disableTouchRipple
          onClick={() => onSaveAccess({})}
          disabled={loading}
          sx={{
            padding: '0.625rem 1.5rem',
            borderRadius: '100px',
            textTransform: 'none',
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            fontWeight: 500,
            background: 'none',
            color: '#51B8B0',
            ':hover': {
              background: 'none',
            },
          }}
        >
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};

export default GrantAccessToProvider;
